import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xArray, xInteger, xNumber, xObject, xString } from './XTypes.js';
import { xBase64ToObject, xObjectToBase64 } from './XUtil.js';

import template from './XImage.html?raw';
import { xIsObjectEmpty } from './XTypeChecks.js';

export class XImage
{
    static get type () { return 'XImage'; }

    static #extensionPriority = ['webp', 'jpg', 'png'];

    static detectAvailableExtensions()
    {
        console.log('TODO');
    }

    #dom;
    #parent;
    #data;
    #element;

    constructor(dom, parent, {
        resizeContainer = environments.active.resizeContainer,
    } = {})
    {
        this.#dom     = dom;
        this.#parent  = parent;
        this.#element = this.#dom.image._element;
        resizeContainer.add(this);
        this.updateImage(xBase64ToObject(this.#dom._element.dataset.xData));
    };

    setEmptyImage()
    {
        if(this.#element.tagName == 'IMG')
        {
            this.#element.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
        }
        else
        {
            delete this.#element.style.backgroundImage;
        }
    }

    updateImage(data)
    {
        if(!data || xIsObjectEmpty(data))
        {
            this.setEmptyImage();
            return;
        }
        this.#data = data;
        const urlAsset = environments.active.urlAssets + data.$key + '/' + data.$version + '/' + data.name + '/';
        let extension = null;
        for(const extensionPriority of XImage.#extensionPriority)
        {
            if(this.#data.extensions.includes(extensionPriority))
            {
                extension = extensionPriority;
                break;
            }
        }
        if(!extension)
        {
            this.setEmptyImage();
            return;
        }
        let resolution = 'x';
        if(this.#data.resolutions.length > 0)
        {
            const boundingClientRect = this.#element.getBoundingClientRect();
            const width = boundingClientRect.width * window.devicePixelRatio;
            const height = boundingClientRect.height * window.devicePixelRatio;
            let i;
            for(i = 0; i < this.#data.resolutions.length-1; i++)
            {
                if(width <= this.#data.resolutions[i].width || height <= this.#data.resolutions[i].height)
                {
                    break;
                }
            }
            const bestResolution = this.#data.resolutions[i];
            resolution = String(bestResolution.width) + 'x' + String(bestResolution.height);
        }
        const src = urlAsset + resolution + '.' + extension;
        if(this.#element.tagName == 'IMG')
        {
            this.#element.src = src;
        }
        else
        {
            this.#element.style.backgroundImage = 'url(' + src + ')';
        }
    }

    _handleResize()
    {
        this.updateImage(this.#data);
    }

    _destroy()
    {
        this._resizeContainer?.remove(this);
        this.#dom._destroy();
    }

    get _dom   () { return this.#dom;    }
    get parent () { return this.#parent; }
    get data   () { return this.#data;   }
};

export class XImageReactive extends XImage
{
    static get specification () 
    {
        return xObject({
            $: XImage.type,
            extensions: xArray(xString()),
            alt: xString(),
            name: xString(),
            color: xString('#ffffff'),
            fontLineHeight: xNumber(0),
            resolutions: xArray(xObject({
                width: xInteger(),
                height: xInteger()
            }))
        });
    }

    static get templateIMG () { return XImageReactive.#template.IMG; }
    static get templateDIV () { return XImageReactive.#template.DIV; }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XImageReactive.specification, domEnvironment);
    }

    #data;
    #usedTemplate

    constructor(data, parent, {
        resizeContainer = environments.active.resizeContainer,
        template        = XImageReactive.templateIMG
    } = {})
    {
        super(template._createAccessibleDOM(data), parent, {
            resizeContainer: resizeContainer
        });
        this.#data = data;
        this.#usedTemplate = template;
        this.#data._enableParenting();
        this._dom._attach({
            _changed: () => {
                this.updateImage(this.storageData);
            }
        });
    };

    get storageData()
    {
        const data = this.#data.toJSON();
        data.$key = this.#data._key;
        data.$version = this.#data._version;
        return data;
    }

    _render()
    {
        const dom = this.#usedTemplate._createAccessibleDOM(this.#data);
        dom._element.dataset.xData = xObjectToBase64(this.storageData);
        return dom;
    }

    get data () { return this.#data; }
};
